import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Skeleton from "@material-ui/lab/Skeleton";

import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import WifiTetheringIcon from "@material-ui/icons/WifiTethering";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import SortIcon from "@material-ui/icons/Sort";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import CloseIcon from "@material-ui/icons/Close";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import ModalAstro from "../../components/ModalAstro";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ehAdmin, rotuloPerfil } from "../../utils/perfil";
import { bannerAstro, painelAstro } from "../../utils/estiloAstro";
import { bannerNavy } from "../../utils/estiloNavy";

const STATUS_OPCOES = [
  { valor: "disponivel", rotulo: "Disponível", cor: "#1E9855" },
  { valor: "almoco", rotulo: "Em almoço", cor: "#E8A93B" },
  { valor: "ausente", rotulo: "Ausente", cor: "#ef4444" },
];

const PERFIS = [
  { valor: "admin", rotulo: "Administrador", cor: "#dc2626" },
  { valor: "operador", rotulo: "Operador", cor: "#ea580c" },
  { valor: "user", rotulo: "Usuário Comum", cor: "#2563eb" },
];

const ORDENS = [
  { valor: "az", rotulo: "Nome (A → Z)" },
  { valor: "za", rotulo: "Nome (Z → A)" },
  { valor: "online", rotulo: "Online primeiro" },
  { valor: "perfil", rotulo: "Por perfil" },
];

const opcaoDisp = (valor) =>
  STATUS_OPCOES.find((o) => o.valor === (valor || "disponivel")) || STATUS_OPCOES[0];

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = { ...state[userIndex], ...user };
      return [...state];
    } else {
      return state;
    }
  }

  if (action.type === "CREATE_USER") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = { ...state[userIndex], ...user };
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// colunas da lista: nome | status | disponibilidade | perfil | ações
const COLUNAS = "minmax(240px, 2.2fr) 110px minmax(150px, 1fr) 150px 128px";

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(148,163,184,.14)" : "#e3ece6";
  const fundoSuave = escuro ? "rgba(255,255,255,.03)" : "#f8fbf9";
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      padding: theme.spacing(2.5, 2.5, 2),
      minHeight: "100%",
    },

    // ---------- banner ----------
    banner: {
      ...bannerNavy(theme),
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: theme.spacing(2.5),
    },
    bannerTitulo: {
      fontWeight: 800,
      fontSize: "1.35rem",
      lineHeight: 1.2,
    },
    stats: {
      display: "flex",
      flexWrap: "wrap",
      gap: 10,
      marginLeft: "auto",
    },
    stat: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 112,
      padding: "8px 14px 8px 10px",
      borderRadius: 14,
      background: "rgba(255,255,255,.12)",
      border: "1px solid rgba(255,255,255,.2)",
      animation: "iaSubir .4s ease backwards",
    },
    statIcone: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(255,255,255,.18)",
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    statNumero: { fontWeight: 900, fontSize: "1.15rem", lineHeight: 1, color: "#fff" },
    statRotulo: {
      fontSize: "0.64rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: ".06em",
      color: "rgba(255,255,255,.8)",
    },
    botaoAdicionar: {
      textTransform: "none",
      fontWeight: 800,
      borderRadius: 12,
      padding: "9px 18px",
    },

    // ---------- filtros (uma linha de selects) ----------
    filtros: {
      ...painelAstro(theme),
      padding: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      animation: "iaSubir .4s .05s ease backwards",
    },
    busca: {
      flex: "1 1 240px",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: fundoSuave,
        transition: "box-shadow .2s ease",
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(34,197,94,.14)" },
      },
      "& input": { padding: "10px 0", fontSize: "0.88rem" },
    },
    select: {
      minWidth: 160,
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: fundoSuave,
        transition: "box-shadow .2s ease, background-color .2s ease",
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(34,197,94,.14)" },
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: borda },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 32px 10px 12px",
        fontSize: "0.85rem",
        fontWeight: 600,
        "&:focus": { backgroundColor: "transparent" },
      },
    },
    // filtro em uso: borda e fundo verdes
    selectAtivo: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: escuro ? "rgba(34,197,94,.10)" : "#effaf3",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: escuro ? "rgba(74,222,128,.45) !important" : "#86d3a5 !important",
      },
    },
    ponto: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    qtdMenu: {
      marginLeft: "auto",
      paddingLeft: 16,
      fontSize: "0.72rem",
      fontWeight: 700,
      color: theme.palette.text.secondary,
    },
    limpar: {
      width: 40,
      height: 40,
      borderRadius: 12,
      color: theme.palette.text.secondary,
      border: `1px solid ${borda}`,
      "&:hover": { color: "#dc2626", backgroundColor: escuro ? "rgba(239,68,68,.12)" : "#fef2f2" },
    },

    // ---------- lista ----------
    lista: {
      ...painelAstro(theme),
      minHeight: 280,
      overflowX: "auto",
      ...theme.scrollbarStyles,
      animation: "iaSubir .4s .1s ease backwards",
    },
    listaInterna: { minWidth: 820 },
    cabecalhoLista: {
      position: "sticky",
      top: 0,
      zIndex: 1,
      display: "grid",
      gridTemplateColumns: COLUNAS,
      alignItems: "center",
      gap: 12,
      padding: "12px 20px",
      backgroundColor: escuro ? "#18201c" : "#f4f8f6",
      borderBottom: `1px solid ${borda}`,
      fontSize: "0.68rem",
      fontWeight: 800,
      letterSpacing: ".08em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    linha: {
      position: "relative",
      display: "grid",
      gridTemplateColumns: COLUNAS,
      alignItems: "center",
      gap: 12,
      padding: "12px 20px",
      borderBottom: `1px solid ${escuro ? "rgba(255,255,255,.05)" : "#eef3f0"}`,
      animation: "iaSubir .3s ease backwards",
      transition: "background-color .15s ease",
      // barrinha verde à esquerda no hover
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 10,
        bottom: 10,
        width: 3,
        borderRadius: 3,
        background: "linear-gradient(180deg, #22c55e, #16a34a)",
        opacity: 0,
        transition: "opacity .15s ease",
      },
      "&:hover": { backgroundColor: fundoSuave },
      "&:hover::before": { opacity: 1 },
      "&:hover $acoes": { opacity: 1 },
    },
    usuario: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      minWidth: 0,
    },
    avatarWrap: {
      position: "relative",
      flexShrink: 0,
    },
    avatar: {
      width: 42,
      height: 42,
      fontSize: "1rem",
      fontWeight: 800,
      color: "#fff",
      borderRadius: 13,
      background: "linear-gradient(135deg, #0f5f35 0%, #1E9855 55%, #4ade80 120%)",
      boxShadow: "0 6px 14px rgba(30,152,85,.22)",
    },
    avatarOff: {
      background: escuro
        ? "linear-gradient(135deg, #334155, #475569)"
        : "linear-gradient(135deg, #94a3b8, #cbd5e1)",
      boxShadow: "none",
    },
    pontoAvatar: {
      position: "absolute",
      right: -3,
      bottom: -3,
      width: 13,
      height: 13,
      borderRadius: "50%",
      border: `2.5px solid ${theme.palette.background.paper}`,
    },
    nome: {
      fontWeight: 800,
      fontSize: "0.92rem",
      lineHeight: 1.25,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    email: {
      fontSize: "0.76rem",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    voce: {
      marginLeft: 6,
      padding: "1px 7px",
      borderRadius: 999,
      fontSize: "0.6rem",
      fontWeight: 800,
      verticalAlign: "middle",
      color: "#16a34a",
      backgroundColor: escuro ? "rgba(34,197,94,.16)" : "#e7f8ee",
    },
    pilula: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: "0.72rem",
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    // anel que pulsa (transform/opacity — leve)
    pontoVivo: {
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: "inherit",
        animation: "iaPulso 2.4s ease-out infinite",
      },
    },
    selectDisp: {
      borderRadius: 999,
      fontSize: "0.76rem",
      fontWeight: 700,
      border: `1px solid ${borda}`,
      backgroundColor: theme.palette.background.paper,
      transition: "border-color .15s ease",
      "&:hover": { borderColor: escuro ? "rgba(74,222,128,.4)" : "#a7dcbc" },
      "& .MuiSelect-select": {
        padding: "5px 28px 5px 12px",
        borderRadius: 999,
        "&:focus": { backgroundColor: "transparent" },
      },
      "& .MuiSelect-icon": { right: 4, fontSize: 18 },
    },
    tracinho: { fontSize: "0.8rem", color: theme.palette.text.disabled },
    acoes: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 4,
      opacity: 0.75,
      transition: "opacity .15s ease",
    },
    acao: {
      width: 34,
      height: 34,
      borderRadius: 10,
      border: `1px solid ${borda}`,
      color: theme.palette.text.secondary,
      transition: "background-color .15s ease, color .15s ease, border-color .15s ease",
      "& .MuiSvgIcon-root": { fontSize: 18 },
      "&:hover": {
        color: "#16a34a",
        borderColor: escuro ? "rgba(74,222,128,.4)" : "#a7dcbc",
        backgroundColor: escuro ? "rgba(34,197,94,.12)" : "#ecf9f1",
      },
    },
    acaoPerigo: {
      "&:hover": {
        color: "#dc2626",
        borderColor: escuro ? "rgba(248,113,113,.4)" : "#fecaca",
        backgroundColor: escuro ? "rgba(239,68,68,.12)" : "#fef2f2",
      },
    },
    vazio: {
      padding: theme.spacing(8, 3),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    vazioIcone: {
      width: 68,
      height: 68,
      margin: "0 auto 14px",
      borderRadius: 22,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#22c55e",
      backgroundColor: escuro ? "rgba(34,197,94,.12)" : "#e7f8ee",
      animation: "iaFlutuar 2.8s ease-in-out infinite",
      "& .MuiSvgIcon-root": { fontSize: 34 },
    },
    rodape: {
      padding: "10px 20px",
      fontSize: "0.74rem",
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    aviso: {
      display: "flex",
      gap: 14,
      alignItems: "flex-start",
      padding: theme.spacing(2),
      borderRadius: 14,
      backgroundColor: escuro ? "rgba(239,68,68,.08)" : "#fef2f2",
      border: `1px solid ${escuro ? "rgba(239,68,68,.25)" : "#fecaca"}`,
      "& > .MuiSvgIcon-root": { color: "#ef4444", fontSize: 28 },
    },
  };
});

const corPerfil = (perfil) => (PERFIS.find((p) => p.valor === perfil) || PERFIS[2]).cor;

const Users = () => {
  const classes = useStyles();
  // Operador ve a equipe e mexe na disponibilidade/cadastro dos usuarios
  // comuns, mas nao cria, nao exclui e nao mexe em admin/operador.
  const { user: logado } = useContext(AuthContext);
  const souAdmin = ehAdmin(logado?.profile);
  const podeMexerEm = (alvo) => souAdmin || alvo?.profile === "user";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [forcingLogoutUser, setForcingLogoutUser] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [dispFilter, setDispFilter] = useState("all");
  const [ordem, setOrdem] = useState("az");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber, status: statusFilter, t: new Date().getTime() },
          });
          dispatch({ type: "LOAD_USERS", payload: data.users });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter]);

  useEffect(() => {
    const socket = openSocket();

    const onUser = (data) => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "create") {
        dispatch({ type: "CREATE_USER", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    socket.on("user", onUser);

    return () => {
      socket.off("user", onUser);
    };
  }, []);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const fecharConfirmacao = () => {
    setDeletingUser(null);
    setForcingLogoutUser(null);
  };

  const handleDeleteUser = async (userId) => {
    setConfirmando(true);
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setConfirmando(false);
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleChangeDisponibilidade = async (userId, disponibilidade) => {
    try {
      await api.put(`/users/${userId}/status`, { disponibilidade });
    } catch (err) {
      toastError(err);
    }
  };

  const handleForceLogout = async (userId) => {
    setConfirmando(true);
    try {
      await api.post(`/users/${userId}/logout`);
      toast.success("Usuário desconectado com sucesso.");
    } catch (err) {
      toastError(err);
    }
    setConfirmando(false);
    setForcingLogoutUser(null);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const limparFiltros = () => {
    setSearchParam("");
    setStatusFilter("all");
    setProfileFilter("all");
    setDispFilter("all");
    setOrdem("az");
  };

  const filtrosAtivos =
    !!searchParam || statusFilter !== "all" || profileFilter !== "all" || dispFilter !== "all";

  // contagens sobre o que já foi carregado
  const contagem = useMemo(() => {
    const c = { total: users.length, online: 0, disponiveis: 0, admins: 0, perfil: {}, disp: {} };
    users.forEach((u) => {
      if (u.online) c.online += 1;
      if (u.profile === "admin") c.admins += 1;
      c.perfil[u.profile] = (c.perfil[u.profile] || 0) + 1;
      if (u.profile !== "admin") {
        const d = u.disponibilidade || "disponivel";
        c.disp[d] = (c.disp[d] || 0) + 1;
        if (d === "disponivel") c.disponiveis += 1;
      }
    });
    return c;
  }, [users]);

  const lista = useMemo(() => {
    const ordemPerfil = { admin: 0, operador: 1, user: 2 };
    const porNome = (a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR");
    return users
      .filter((u) => profileFilter === "all" || u.profile === profileFilter)
      .filter(
        (u) =>
          dispFilter === "all" ||
          (u.profile !== "admin" && (u.disponibilidade || "disponivel") === dispFilter)
      )
      .sort((a, b) => {
        if (ordem === "za") return porNome(b, a);
        if (ordem === "online") return (b.online ? 1 : 0) - (a.online ? 1 : 0) || porNome(a, b);
        if (ordem === "perfil")
          return (ordemPerfil[a.profile] ?? 3) - (ordemPerfil[b.profile] ?? 3) || porNome(a, b);
        return porNome(a, b);
      });
  }, [users, profileFilter, dispFilter, ordem]);

  // Select de filtro: "all" mostra o nome do filtro; opção escolhida mostra bolinha + rótulo
  const selectFiltro = ({ valor, mudar, Icone, rotuloTodos, opcoes, semTodos }) => {
    const ativo = !semTodos && valor !== "all";
    return (
      <TextField
        select
        variant="outlined"
        className={`${classes.select} ${ativo ? classes.selectAtivo : ""}`}
        value={valor}
        onChange={(e) => mudar(e.target.value)}
        SelectProps={{
          MenuProps: {
            getContentAnchorEl: null,
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            PaperProps: { style: { borderRadius: 12, marginTop: 6, minWidth: 200 } },
          },
          renderValue: (v) => {
            const op = opcoes.find((o) => o.valor === v);
            return (
              <>
                {op?.cor ? (
                  <span className={classes.ponto} style={{ backgroundColor: op.cor }} />
                ) : (
                  <Icone fontSize="small" style={{ color: "#22c55e" }} />
                )}
                {op ? op.rotulo : rotuloTodos}
              </>
            );
          },
        }}
      >
        {!semTodos && (
          <MenuItem value="all" style={{ fontSize: "0.86rem" }}>
            Todos
          </MenuItem>
        )}
        {opcoes.map((o) => (
          <MenuItem key={o.valor} value={o.valor} style={{ fontSize: "0.86rem", gap: 8 }}>
            {o.cor && <span className={classes.ponto} style={{ backgroundColor: o.cor }} />}
            {o.rotulo}
            {o.qtd != null && <span className={classes.qtdMenu}>{o.qtd}</span>}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const stats = [
    { rotulo: "Usuários", valor: contagem.total, Icone: PeopleAltOutlinedIcon },
    { rotulo: "Online", valor: contagem.online, Icone: WifiTetheringIcon },
    { rotulo: "Disponíveis", valor: contagem.disponiveis, Icone: CheckCircleOutlineIcon },
    { rotulo: "Admins", valor: contagem.admins, Icone: SecurityOutlinedIcon },
  ];

  const alvoConfirmacao = deletingUser || forcingLogoutUser;

  return (
    <MainContainer onScroll={handleScroll}>
      <ModalAstro
        open={!!alvoConfirmacao}
        onClose={fecharConfirmacao}
        maxWidth="xs"
        icone={deletingUser ? <DeleteOutlineIcon /> : <ExitToAppIcon />}
        titulo={
          deletingUser
            ? `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
            : forcingLogoutUser
            ? `Desconectar ${forcingLogoutUser.name}?`
            : ""
        }
        subtitulo={alvoConfirmacao?.email}
        acoes={
          <>
            <Button onClick={fecharConfirmacao}>Cancelar</Button>
            <Button
              variant="contained"
              disabled={confirmando}
              startIcon={
                confirmando ? <CircularProgress size={14} color="inherit" /> : deletingUser ? <DeleteOutlineIcon /> : <ExitToAppIcon />
              }
              style={{
                background: "linear-gradient(135deg, #dc2626, #ef4444)",
                color: "#fff",
                boxShadow: "0 8px 20px rgba(239,68,68,.3)",
              }}
              onClick={() => {
                if (deletingUser) handleDeleteUser(deletingUser.id);
                else if (forcingLogoutUser) handleForceLogout(forcingLogoutUser.id);
              }}
            >
              {deletingUser ? "Excluir" : "Desconectar"}
            </Button>
          </>
        }
      >
        <div className={classes.aviso}>
          <WarningRoundedIcon />
          <Typography variant="body2" style={{ lineHeight: 1.55 }}>
            {deletingUser
              ? i18n.t("users.confirmationModal.deleteMessage")
              : "Tem certeza que deseja forçar a saída deste usuário do sistema?"}
          </Typography>
        </div>
      </ModalAstro>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />

      <div className={classes.root}>
        {/* Banner com resumo da equipe */}
        <div className={classes.banner}>
          <div>
            <Typography className={classes.bannerTitulo}>Equipe</Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Gerencie quem acessa o sistema, perfis e disponibilidade para atendimento.
            </Typography>
          </div>
          <div className={classes.stats}>
            {stats.map((s, i) => (
              <div key={s.rotulo} className={classes.stat} style={{ animationDelay: `${0.05 + i * 0.05}s` }}>
                <div className={classes.statIcone}>
                  <s.Icone />
                </div>
                <div>
                  <div className={classes.statNumero}>{s.valor}</div>
                  <div className={classes.statRotulo}>{s.rotulo}</div>
                </div>
              </div>
            ))}
          </div>
          {souAdmin && (
            <Button
              variant="contained"
              color="primary"
              className={classes.botaoAdicionar}
              startIcon={<PersonAddOutlinedIcon />}
              onClick={handleOpenUserModal}
            >
              {i18n.t("users.buttons.add")}
            </Button>
          )}
        </div>

        {/* Filtros: tudo em uma linha */}
        <div className={classes.filtros}>
          <TextField
            className={classes.busca}
            variant="outlined"
            placeholder="Buscar por nome ou e-mail..."
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: "#22c55e" }} />
                </InputAdornment>
              ),
              endAdornment: searchParam ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchParam("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {selectFiltro({
            valor: statusFilter,
            mudar: setStatusFilter,
            Icone: WifiTetheringIcon,
            rotuloTodos: "Status",
            opcoes: [
              { valor: "online", rotulo: "Online", cor: "#22c55e" },
              { valor: "offline", rotulo: "Offline", cor: "#94a3b8" },
            ],
          })}

          {selectFiltro({
            valor: dispFilter,
            mudar: setDispFilter,
            Icone: CheckCircleOutlineIcon,
            rotuloTodos: "Disponibilidade",
            opcoes: STATUS_OPCOES.map((o) => ({ ...o, qtd: contagem.disp[o.valor] || 0 })),
          })}

          {selectFiltro({
            valor: profileFilter,
            mudar: setProfileFilter,
            Icone: SecurityOutlinedIcon,
            rotuloTodos: "Perfil",
            opcoes: PERFIS.map((p) => ({ ...p, qtd: contagem.perfil[p.valor] || 0 })),
          })}

          {selectFiltro({
            valor: ordem,
            mudar: setOrdem,
            Icone: SortIcon,
            semTodos: true,
            opcoes: ORDENS,
          })}

          {filtrosAtivos && (
            <Tooltip title="Limpar filtros" arrow>
              <IconButton className={classes.limpar} onClick={limparFiltros}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {/* Lista */}
        <div className={classes.lista}>
          <div className={classes.listaInterna}>
            <div className={classes.cabecalhoLista}>
              <span>{i18n.t("users.table.name")}</span>
              <span>Status</span>
              <span>Disponibilidade</span>
              <span>{i18n.t("users.table.profile")}</span>
              <span style={{ textAlign: "right" }}>{i18n.t("users.table.actions")}</span>
            </div>

            {lista.map((user, i) => {
              const disp = opcaoDisp(user.disponibilidade);
              const cor = corPerfil(user.profile);
              return (
                <div
                  key={user.id}
                  className={classes.linha}
                  style={{ animationDelay: `${Math.min(i, 10) * 0.03}s` }}
                >
                  <div className={classes.usuario}>
                    <div className={classes.avatarWrap}>
                      <Avatar
                        variant="rounded"
                        className={`${classes.avatar} ${user.online ? "" : classes.avatarOff}`}
                      >
                        {(user.name || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <span
                        className={classes.pontoAvatar}
                        style={{ backgroundColor: user.online ? "#22c55e" : "#94a3b8" }}
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <Typography className={classes.nome}>
                        {user.name}
                        {logado?.id === user.id && <span className={classes.voce}>Você</span>}
                      </Typography>
                      <Typography className={classes.email}>{user.email}</Typography>
                    </div>
                  </div>

                  <div>
                    <span
                      className={classes.pilula}
                      style={{
                        backgroundColor: user.online ? "rgba(34,197,94,.13)" : "rgba(148,163,184,.15)",
                        color: user.online ? "#16a34a" : "#8a9a91",
                      }}
                    >
                      <span
                        className={`${classes.ponto} ${user.online ? classes.pontoVivo : ""}`}
                        style={{ backgroundColor: user.online ? "#22c55e" : "#94a3b8" }}
                      />
                      {user.online ? "Online" : "Offline"}
                    </span>
                  </div>

                  <div>
                    {user.profile === "admin" ? (
                      <span className={classes.tracinho}>—</span>
                    ) : !podeMexerEm(user) ? (
                      <span
                        className={classes.pilula}
                        style={{ backgroundColor: `${disp.cor}1f`, color: disp.cor }}
                      >
                        <span className={classes.ponto} style={{ backgroundColor: disp.cor }} />
                        {disp.rotulo}
                      </span>
                    ) : (
                      <Select
                        value={user.disponibilidade || "disponivel"}
                        onChange={(e) => handleChangeDisponibilidade(user.id, e.target.value)}
                        disableUnderline
                        className={classes.selectDisp}
                        MenuProps={{
                          getContentAnchorEl: null,
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                          PaperProps: { style: { borderRadius: 12, marginTop: 6 } },
                        }}
                        renderValue={(val) => {
                          const op = opcaoDisp(val);
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: op.cor }}>
                              <span className={classes.ponto} style={{ backgroundColor: op.cor }} />
                              {op.rotulo}
                            </span>
                          );
                        }}
                      >
                        {STATUS_OPCOES.map((op) => (
                          <MenuItem key={op.valor} value={op.valor} style={{ fontSize: "0.86rem" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <span className={classes.ponto} style={{ backgroundColor: op.cor }} />
                              {op.rotulo}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </div>

                  <div>
                    <span
                      className={classes.pilula}
                      style={{ backgroundColor: `${cor}17`, color: cor, border: `1px solid ${cor}33` }}
                    >
                      {rotuloPerfil(user.profile)}
                    </span>
                  </div>

                  <div className={classes.acoes}>
                    {user.online && podeMexerEm(user) && (
                      <Tooltip title="Desconectar usuário" arrow>
                        <IconButton
                          className={`${classes.acao} ${classes.acaoPerigo}`}
                          onClick={() => setForcingLogoutUser(user)}
                        >
                          <ExitToAppIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {podeMexerEm(user) && (
                      <Tooltip title="Editar" arrow>
                        <IconButton className={classes.acao} onClick={() => handleEditUser(user)}>
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {souAdmin && (
                      <Tooltip title="Excluir" arrow>
                        <IconButton
                          className={`${classes.acao} ${classes.acaoPerigo}`}
                          onClick={() => setDeletingUser(user)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}

            {loading &&
              [0, 1, 2].map((k) => (
                <div key={`sk-${k}`} className={classes.linha} style={{ animation: "none" }}>
                  <div className={classes.usuario}>
                    <Skeleton variant="rect" width={42} height={42} style={{ borderRadius: 13 }} />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="60%" />
                      <Skeleton width="80%" height={14} />
                    </div>
                  </div>
                  <Skeleton width={70} height={26} style={{ borderRadius: 999 }} />
                  <Skeleton width={110} height={26} style={{ borderRadius: 999 }} />
                  <Skeleton width={110} height={26} style={{ borderRadius: 999 }} />
                  <Skeleton width={90} height={34} style={{ marginLeft: "auto" }} />
                </div>
              ))}

            {!loading && lista.length === 0 && (
              <div className={classes.vazio}>
                <div className={classes.vazioIcone}>
                  <PersonOutlineIcon />
                </div>
                <Typography style={{ fontWeight: 800 }}>Nenhum usuário encontrado</Typography>
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  Ajuste os filtros ou a busca para ver mais resultados.
                </Typography>
                {filtrosAtivos && (
                  <Button
                    startIcon={<ClearAllIcon />}
                    onClick={limparFiltros}
                    style={{ marginTop: 14, textTransform: "none", fontWeight: 700, borderRadius: 10 }}
                    color="primary"
                    variant="outlined"
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            )}

            {!loading && lista.length > 0 && (
              <div className={classes.rodape}>
                Mostrando {lista.length} de {users.length} usuário{users.length === 1 ? "" : "s"}
                {hasMore ? " — role para carregar mais" : ""}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Users;
